import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { createPath, parsePath } from 'history';

const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { RESULT_TYPE } = MojitoServices.Search.types;

/**
 * Navigation and URL helpers shared by the inline `SearchResultItem` and the discovery `SearchResultRow`.
 *
 * @class SearchUtils
 * @memberof Mojito.Applications.Sports.Search
 */
export default {
    /**
     * Append an encoded query parameter to a URL. Preserves any existing query string and hash, and
     * relies on `URLSearchParams` for encoding so callers do not have to encode values themselves.
     *
     * @param {string} url - URL to update.
     * @param {string} key - Query parameter name.
     * @param {string} value - Query parameter value.
     * @returns {string} URL with the appended query parameter.
     *
     * @function Mojito.Applications.Sports.Search.SearchUtils#appendQueryParam
     */
    appendQueryParam(url, key, value) {
        const { pathname, search = '', hash = '' } = parsePath(url);
        const params = new URLSearchParams(search);
        params.append(key, value);
        return createPath({ pathname, search: params.toString(), hash });
    },

    /**
     * Resolve navigation data for a search result.
     *
     * @param {object} result - Search result.
     * @param {string} result.id - Result id.
     * @param {string} result.sportId - Sport id.
     * @param {string} [result.raceMeetingId] - Race meeting id.
     * @param {Mojito.Services.Search.types.RESULT_TYPE} result.resultType - Result type.
     * @param {boolean} [result.inPlay] - Whether the event is in play.
     * @param {boolean} [result.isOutright] - Whether the competition is an outright.
     * @returns {object|undefined} Navigation data for the result type.
     *
     * @function Mojito.Applications.Sports.Search.SearchUtils#getResultNavigationData
     */
    getResultNavigationData({ id, sportId, raceMeetingId, resultType, inPlay, isOutright }) {
        switch (resultType) {
            case RESULT_TYPE.SPORT:
                return NavigationPathBuilder.toSport(sportId);

            case RESULT_TYPE.TYPE:
                return isOutright
                    ? NavigationPathBuilder.toSportOutrightsLeague(sportId, id)
                    : NavigationPathBuilder.toSportCompetitionsLeague(sportId, id);

            case RESULT_TYPE.EVENT:
                if (raceMeetingId) {
                    return NavigationPathBuilder.toRaceEvent(sportId, raceMeetingId, id);
                }
                return inPlay
                    ? NavigationPathBuilder.toInplayEvent(id)
                    : NavigationPathBuilder.toSportEvent(sportId, id);

            case RESULT_TYPE.RACE_MEETING:
                return NavigationPathBuilder.toRaceMeeting(sportId, id);
        }
    },
};
